import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, ScrollView, Platform, Switch, TouchableOpacity, Modal, FlatList, TextInput as RNTextInput } from 'react-native';
import { useTheme } from '../../../../theme/useTheme';
import ConfirmDiscardModal from '../../../../components/ConfirmDiscardModal';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useDispatch, useSelector } from 'react-redux';
import LHOverlay from '../../../../components/LHOverlay';
import LhTextInput from '../../../../components/LhTextInput';
import LHDropDown from '../../../../components/LHDropDown';
import LhButton from '../../../../components/LhButton';
import FontFamily from '../../../../constants/fonts';
import { AppDispatch, RootState } from '../../../../store';
import {
  createObservationQuestion,
  updateObservationQuestion,
  patchObsStatus,
  fetchRolesForFilters,
  type RoleFilterOption,
} from '../../../../store/slices/adminSlice';
import {
  LOCATION_TYPE_OPTIONS,
  ORGANIZATION_TYPE_OPTIONS,
  THREE_PL_OPTIONS,
  SPARC_CATEGORY_OPTIONS,
  OBSERVATION_TYPE_OPTIONS,
  OBSERVATION_SECTION_OPTIONS,
} from './kfaDropdownOptions';
import { getValidationState, REQUIRED_FIELDS_MESSAGE } from './validationUtils';
import { styles, msStyles } from './ObservationAddOverlay.styles';





// ─── Types ────────────────────────────────────────────────────────────────────

/** Full dataset for edit mode — mirrors the API response fields */
export interface ObservationEditItem {
  id: string;
  /** Maps to `observation` in the POST payload */
  observation: string;
  /** Maps to `question` in the POST payload */
  question: string;
  currentCategory?: string;
  sparcCategory?: string;
  locationType?: string;
  /** Single string from table row — wrapped in orgTypes[] for POST */
  organizationType?: string;
  /** true → threePlStatus:"YES", false → "NO" */
  is3pl?: boolean;
  /** API string value: "YES" | "NO" | "ANY" — takes precedence over is3pl */
  threePlStatus?: string;
  status?: boolean;
  // Observation-specific
  /** Maps to observationTypeNames[] */
  observationTypeNames?: string[];
  /** Maps to roleNames[] */
  roleNames?: string[];
  section?: string;
  columnTag?: number;
  buildingTag?: number;
  questionNumber?: number;
  contentType?: string;
  material?: string;
  palletType?: string;
  /** ISO timestamp from the API — used to enforce the 24-hour edit lock on text fields */
  createdAt?: string;
}

interface ObservationAddOverlayProps {
  visible: boolean;
  onDismiss: () => void;
  /** 'create' opens a blank form; 'edit' pre-fills from selectedItem */
  mode?: 'create' | 'edit';
  selectedItem?: ObservationEditItem;
  /** Called after a successful save so the parent can re-fetch the table */
  onSaveSuccess?: () => void;
}

interface ObservationValidationErrors {
  observation: boolean;
  question: boolean;
  sparcCategory: boolean;
  locationType: boolean;
  organizationType: boolean;
  threeplStatus: boolean;
  observationTypes: boolean;
  section: boolean;
  roles: boolean;
}

interface RoleDropdownOption {
  id: string;
  displayText: string;
}

// ─── MSOption + MultiSelectDropdown ──────────────────────────────────────────

interface MSOption {
  id: string | number;
  displayText: string;
}

interface MultiSelectDropdownProps {
  label?: string;
  options: MSOption[];
  selected: MSOption[];
  onChange: (selected: MSOption[]) => void;
  placeholder?: string;
  singleSelect?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label, options, selected, onChange, placeholder = 'Select...', singleSelect = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [menuStyle, setMenuStyle] = React.useState({ top: 0, left: 0, width: 200 });
  const triggerRef = React.useRef<any>(null);

  const toggle = () => {
    if (!open && triggerRef.current) {
      if (Platform.OS === 'web') {
        try {
          const rect = (triggerRef.current as any).getBoundingClientRect();
          const scrollY = typeof window !== 'undefined' ? window.scrollY || 0 : 0;
          setMenuStyle({ top: rect.bottom + scrollY, left: rect.left, width: rect.width });
        } catch (_) { /* ignore */ }
      } else {
        triggerRef.current.measureInWindow?.((x: number, y: number, w: number, h: number) => {
          setMenuStyle({ top: y + h, left: x, width: w });
        });
      }
    }
    setOpen((o) => !o);
  };

  const isSelected = (item: MSOption) => selected.some((s) => String(s.id) === String(item.id));
  const handlePress = (item: MSOption) => {
    if (singleSelect) {
      onChange([item]);
      setOpen(false);
      setSearch('');
      return;
    }
    if (isSelected(item)) onChange(selected.filter((s) => String(s.id) !== String(item.id)));
    else onChange([...selected, item]);
  };
  const filtered = options.filter((o) => o.displayText.toLowerCase().includes(search.toLowerCase()));
  const displayText = selected.length === 0 ? placeholder : selected.map((s) => s.displayText).join(', ');

  return (
    <View style={msStyles.container}>
      {label ? <Text style={msStyles.label}>{label}</Text> : null}
      <TouchableOpacity ref={triggerRef} style={msStyles.trigger} onPress={toggle} activeOpacity={0.8}>
        <Text style={[msStyles.triggerText, selected.length === 0 && msStyles.placeholder]} numberOfLines={1}>{displayText}</Text>
        <MaterialIcons name={open ? 'arrow-drop-up' : 'arrow-drop-down'} size={20} color="#495057" />
      </TouchableOpacity>
      <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        <TouchableOpacity style={msStyles.backdrop} activeOpacity={1} onPress={() => setOpen(false)} />
        <View style={[msStyles.menu, Platform.OS === 'web'
          ? ({ position: 'fixed', top: menuStyle.top, left: menuStyle.left, width: menuStyle.width } as any)
          : { position: 'absolute', top: menuStyle.top, left: menuStyle.left, width: menuStyle.width }]}>
          <View style={msStyles.searchBox}>
            <MaterialIcons name="search" size={16} color="#888" style={{ marginRight: 6 }} />
            <RNTextInput value={search} onChangeText={setSearch} placeholder="Search..." placeholderTextColor="#AAAAAA" style={msStyles.searchInput} />
          </View>
          <FlatList data={filtered} keyExtractor={(item) => String(item.id)} style={{ maxHeight: 200 }} keyboardShouldPersistTaps="always"
            renderItem={({ item }) => (
              <TouchableOpacity style={[msStyles.option, isSelected(item) && msStyles.optionSelected]} onPress={() => handlePress(item)}>
                <Text style={msStyles.optionText}>{item.displayText}</Text>
                {isSelected(item) && <MaterialIcons name="check" size={18} color="#0066CC" />}
              </TouchableOpacity>
            )} />
        </View>
      </Modal>
    </View>
  );
};


// ─── Helpers ──────────────────────────────────────────────────────────────────

const findOption = <T extends { id: string | number; displayText: string }>(
  options: T[],
  value: string | number | undefined | null,
): T | null => {
  if (value === undefined || value === null || value === '') return null;
  return options.find((o) => String(o.id).toLowerCase() === String(value).toLowerCase()) ?? null;
};

const findOptions = <T extends { id: string | number; displayText: string }>(
  options: T[],
  values: string[] | undefined | null,
): T[] => {
  if (!values || values.length === 0) return [];
  return options.filter((o) =>
    values.some((v) => String(o.id).toLowerCase() === String(v).toLowerCase()),
  );
};

const normalizeToStringArray = (value: unknown): string[] => {
  if (value == null) return [];

  if (Array.isArray(value)) {
    return value.flatMap((item) => normalizeToStringArray(item));
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];

    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.flatMap((item) => normalizeToStringArray(item));
        }
      } catch {
        // Fall back to comma-split
      }
    }

    return trimmed.split(',').map((item) => item.trim()).filter(Boolean);
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const candidateKeys = ['displayText', 'label', 'name', 'value', 'roleName', 'observationTypeName'];

    for (const key of candidateKeys) {
      if (obj[key] != null) {
        return normalizeToStringArray(obj[key]);
      }
    }
  }

  return [String(value).trim()].filter(Boolean);
};

// ─── 3PL Status mapping helpers ──────────────────────────────────────────────

/**
 * Converts API value ("YES" | "NO" | "ANY") → UI dropdown id ("Yes" | "No" | "Any")
 * Returns null for empty / unknown values.
 */
const mapApiToUIThreePl = (value: string | undefined | null): string | null => {
  switch (String(value ?? '').toUpperCase()) {
    case 'YES': return 'Yes';
    case 'NO':  return 'No';
    case 'ANY': return 'Any';
    default:    return null;
  }
};

/**
 * Converts UI dropdown id ("Yes" | "No" | "Any") → API value ("YES" | "NO" | "ANY" | "")
 */
const mapUIToApiThreePl = (id: string | undefined | null): string => {
  switch (String(id ?? '').toLowerCase()) {
    case 'yes': return 'YES';
    case 'no':  return 'NO';
    case 'any': return 'ANY';
    default:    return '';
  }
};

// ─── Component ────────────────────────────────────────────────────────────────

const ObservationAddOverlay: React.FC<ObservationAddOverlayProps> = ({
  visible,
  onDismiss,
  mode = 'create',
  selectedItem,
  onSaveSuccess,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

  const dispatch = useDispatch<AppDispatch>();

  const isSaving = useSelector(
    (state: RootState) => state.configuration.observationsQuestions.isLoading,
  );
  const saveError = useSelector(
    (state: RootState) => state.configuration.observationsQuestions.error,
  );

  // ─── Dynamic Roles — driven by Shared Attributes ───────────────────────────
  const [filteredRoles, setFilteredRoles] = useState<RoleDropdownOption[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  // Track the last param set we fetched; avoids duplicate calls when unrelated state changes
  const lastRolesFetchKey = useRef<string | null>(null);
  const latestRolesRequestIdRef = useRef(0);
  const isDirtyRef = useRef(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const loadRoles = useCallback(async (
    locationSelections: MSOption[],
    organizationSelections: MSOption[],
    tpl: any,
  ) => {
    if (!locationSelections.length || !organizationSelections.length || !tpl) return;

    // Convert 3PL option id ('Yes'/'No'/'Any') to API format ('YES'/'NO'/'ANY')
    const threePlParam = mapUIToApiThreePl(tpl?.id);
    const locationIds = locationSelections
      .map((item) => String(item?.id ?? '').trim())
      .filter(Boolean)
      .sort();
    const organizationIds = organizationSelections
      .map((item) => String(item?.id ?? '').trim())
      .filter(Boolean)
      .sort();
    const fetchKey = `${locationIds.join('|')}__${organizationIds.join('|')}__${threePlParam}`;

    if (lastRolesFetchKey.current === fetchKey) return; // nothing changed
    lastRolesFetchKey.current = fetchKey;
    const requestId = ++latestRolesRequestIdRef.current;

    setRolesLoading(true);
    setFilteredRoles([]);
    // Do NOT clear roles here — edit-mode prefill may have already set a value.
    // We'll reconcile against the loaded list once the API responds.
    try {
      const result: RoleFilterOption[] = await dispatch(
        fetchRolesForFilters({
          locationTypes: locationIds,
          organizationTypes: organizationIds,
          threePlStatus: threePlParam,
        }),
      ).unwrap();
      const mappedRoles: RoleDropdownOption[] =
        (result ?? []).map((r) => ({
          id: r.value,
          displayText: r.label,
        }));

      if (requestId !== latestRolesRequestIdRef.current) {
        return;
      }

      setFilteredRoles(mappedRoles);
      // Re-match previous multi-selection by display name so edit prefill survives.
      setRoles((prev: MSOption[]) => {
        if (!prev.length) return [];
        return prev.map((p) => {
          const prevName = String(p.displayText ?? p.id).toLowerCase().trim();
          return mappedRoles.find((r) => r.displayText.toLowerCase().trim() === prevName) ?? p;
        });
      });
    } catch {
      // Graceful degradation — leave filteredRoles empty; user can still proceed
      if (requestId !== latestRolesRequestIdRef.current) {
        return;
      }
      setFilteredRoles([]);
    } finally {
      if (requestId === latestRolesRequestIdRef.current) {
        setRolesLoading(false);
      }
    }
  }, [dispatch]);

  // ─── Form state ──────────────────
  const [observation, setObservation] = useState('');             // → observation
  const [question, setQuestion] = useState('');                    // → question
  const [sparcCategory, setSparcCategory] = useState<MSOption[]>([]);   // → sparcCategory (also displayed as "Current Category" in the UI)
  const [locationType, setLocationType] = useState<MSOption[]>([]);     // → locationType
  const [organizationType, setOrganizationType] = useState<MSOption[]>([]); // → orgTypes (wrapped as array)
  const [threeplStatus, setThreeplStatus] = useState<any>(null);   // → threePlStatus "YES"|"NO"
  const [statusEnabled, setStatusEnabled] = useState<boolean>(true); // → status

  // Observation-specific (multi-select)
  const [observationTypes, setObservationTypes] = useState<MSOption[]>([]);  // → observationTypeNames[]
  const [roles, setRoles] = useState<MSOption[]>([]);                         // → roleNames[]
  const [section, setSection] = useState<MSOption[]>([]);                     // → section[]

  // Optional numeric / text fields
  const [columnTag, setColumnTag] = useState('');                  // → columnTag
  const [buildingTag, setBuildingTag] = useState('');              // → buildingTag
  const [questionNumber, setQuestionNumber] = useState('');        // → questionNumber
  const [contentType, setContentType] = useState('');              // → contentType
  const [material, setMaterial] = useState('');                    // → material
  const [palletType, setPalletType] = useState('');                // → palletType
  // 24-hour text-field lock
  const isTextLocked = mode === 'edit' && !!selectedItem?.createdAt
    && (Date.now() - new Date(selectedItem.createdAt).getTime()) > 24 * 60 * 60 * 1000;

  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<ObservationValidationErrors>({
    observation: false,
    question: false,
    sparcCategory: false,
    locationType: false,
    organizationType: false,
    threeplStatus: false,
    observationTypes: false,
    section: false,
    roles: false,
  });

  /** All 3 required shared-attribute fields must be set before we fetch / show Roles */
  const sharedAttributesComplete = !!(locationType.length > 0 && organizationType.length > 0 && threeplStatus);

  // Auto-fetch roles whenever all 3 shared attributes are filled / change
  useEffect(() => {
    if (!visible) return;
    if (locationType.length > 0 && organizationType.length > 0 && threeplStatus) {
      loadRoles(locationType, organizationType, threeplStatus);
    } else {
      // One or more cleared — hide the dropdown and reset
      lastRolesFetchKey.current = null;
      latestRolesRequestIdRef.current += 1;
      setFilteredRoles([]);
      setRoles([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationType, organizationType, threeplStatus, visible]);

  const clearFieldError = (field: keyof ObservationValidationErrors) => {
    isDirtyRef.current = true;
    setFieldErrors((prev) => ({ ...prev, [field]: false }));
  };

  const validateForm = () => {
    const { fieldErrors: nextErrors, hasErrors } = getValidationState<keyof ObservationValidationErrors>({
      observation: !observation.trim(),
      question: !question.trim(),
      sparcCategory: sparcCategory.length === 0,
      locationType: locationType.length === 0,
      organizationType: organizationType.length === 0,
      threeplStatus: !threeplStatus,
      observationTypes: observationTypes.length === 0,
      section: section.length === 0,
      roles: roles.length === 0,
    });
    setFieldErrors(nextErrors);
    if (hasErrors) {
      setValidationMessage(REQUIRED_FIELDS_MESSAGE);
      return false;
    }
    return true;
  };

  // ─── Scroll-lock (web only) — same pattern as KfaAddOverlay ─────────────────
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (visible) {
      const scrollY = (window as any).scrollY || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      (document.body.dataset as any).obsOverlayScrollY = String(scrollY);
    } else {
      const savedY = parseInt((document.body.dataset as any).obsOverlayScrollY || '0', 10);
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, savedY);
    }
    return () => {
      if (Platform.OS === 'web') {
        const savedY = parseInt((document.body.dataset as any).obsOverlayScrollY || '0', 10);
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, savedY);
      }
    };
  }, [visible]);

  // ─── Reset / Pre-fill on visibility change ───────────────────────────────────
  useEffect(() => {
    if (!visible) {
      setObservation('');
      setQuestion('');
      setSparcCategory([]);
      setLocationType([]);
      setOrganizationType([]);
      setThreeplStatus(null);
      setStatusEnabled(true);
      setObservationTypes([]);
      setRoles([]);
      setSection([]);
      setColumnTag('');
      setBuildingTag('');
      setQuestionNumber('');
      setContentType('');
      setMaterial('');
      setPalletType('');
      setValidationMessage(null);
      setFieldErrors({
        observation: false,
        question: false,
        sparcCategory: false,
        locationType: false,
        organizationType: false,
        threeplStatus: false,
        observationTypes: false,
        section: false,
        roles: false,
      });
      lastRolesFetchKey.current = null;
      latestRolesRequestIdRef.current += 1;
      setFilteredRoles([]);
      setRolesLoading(false);
      isDirtyRef.current = false;
      return;
    }

    if (mode === 'edit' && selectedItem) {
      setObservation(selectedItem.observation ?? '');
      setQuestion(selectedItem.question ?? '');
      // currentCategory and sparcCategory are the same field — prefer sparcCategory per API spec
      // Use findOptions to split comma-separated multi-values (e.g. "Distribution,Manufacturing")
      const splitCSV = (v: string | undefined | null) =>
        v ? String(v).split(',').map((s) => s.trim()).filter(Boolean) : [];
      setSparcCategory(findOptions(SPARC_CATEGORY_OPTIONS, splitCSV(selectedItem.sparcCategory ?? selectedItem.currentCategory)));
      setLocationType(findOptions(LOCATION_TYPE_OPTIONS, splitCSV(selectedItem.locationType)));
      setOrganizationType(findOptions(ORGANIZATION_TYPE_OPTIONS, splitCSV(selectedItem.organizationType)));
      setThreeplStatus(
        selectedItem.threePlStatus
          ? findOption(THREE_PL_OPTIONS, mapApiToUIThreePl(selectedItem.threePlStatus))
          : selectedItem.is3pl !== undefined
            ? findOption(THREE_PL_OPTIONS, selectedItem.is3pl ? 'Yes' : 'No')
            : null,
      );
      setStatusEnabled(selectedItem.status !== false);
      const observationTypeValues = normalizeToStringArray((selectedItem as any).observationTypeNames);
      setObservationTypes(findOptions(OBSERVATION_TYPE_OPTIONS, observationTypeValues));
      // Resolve roles (multi-select) by display name so the dropdown pre-fills immediately.
      const rawRoleValues: string[] = [
        ...normalizeToStringArray((selectedItem as any).roleNames),
        ...normalizeToStringArray((selectedItem as any).obsRoles),
      ];
      const seedRoles: MSOption[] = rawRoleValues
        .map((rv) => rv.trim())
        .filter(Boolean)
        .map((rv) => {
          const match = filteredRoles.find(
            (r) => r.displayText.toLowerCase().trim() === rv.toLowerCase(),
          );
          return match ?? { id: rv, displayText: rv };
        });
      setRoles(seedRoles);
      // Section — multi-select; API may return a comma-separated string or a single value
      const sectionValues = selectedItem.section
        ? String(selectedItem.section).split(',').map((s) => s.trim()).filter(Boolean)
        : [];
      setSection(findOptions(OBSERVATION_SECTION_OPTIONS, sectionValues));
      setColumnTag(selectedItem.columnTag !== undefined ? String(selectedItem.columnTag) : '');
      setBuildingTag(selectedItem.buildingTag !== undefined ? String(selectedItem.buildingTag) : '');
      setQuestionNumber(selectedItem.questionNumber !== undefined ? String(selectedItem.questionNumber) : '');
      setContentType(selectedItem.contentType ?? '');
      setMaterial(selectedItem.material ?? '');
      setPalletType(selectedItem.palletType ?? '');
    } else {
      // create mode — blank slate (already cleared above, but be explicit)
      setObservation('');
      setQuestion('');
      setSparcCategory([]);
      setLocationType([]);
      setOrganizationType([]);
      setThreeplStatus(null);
      setStatusEnabled(true);
      setObservationTypes([]);
      setRoles([]);
      setSection([]);
      setColumnTag('');
      setBuildingTag('');
      setQuestionNumber('');
      setContentType('');
      setMaterial('');
      setPalletType('');
      setValidationMessage(null);
      setFieldErrors({
        observation: false,
        question: false,
        sparcCategory: false,
        locationType: false,
        organizationType: false,
        threeplStatus: false,
        observationTypes: false,
        section: false,
        roles: false,
      });
      lastRolesFetchKey.current = null;
      latestRolesRequestIdRef.current += 1;
      setFilteredRoles([]);
      setRolesLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, mode, selectedItem]);

  // ─── Payload builder ─────────────────────────────────────────────────────────
  const buildPayload = () => {
    // observationTypeNames — multi-select; send all selected ids
    const observationTypeNames: string[] = observationTypes.map((x) => String(x.id));

    // roleNames — multi-select; backend expects role name strings
    const roleNames: string[] = roles.map((x) => String(x.id)).filter(Boolean);

    // section — multi-select; join as comma-separated string per API spec
    const sectionValue = section.map((x) => String(x.id)).join(',');

    // threePlStatus — "YES" | "NO" | "ANY" | "" per API spec
    const threePlStatus = mapUIToApiThreePl(threeplStatus?.id);

    const payload = {
      question: question.trim(),
      observation: observation.trim(),
      observationTypeNames,
      section: sectionValue,
      material: material.trim(),
      palletType: palletType.trim(),
      sparcCategory: sparcCategory.map((x) => String(x.id)).join(','),
      columnTag: columnTag !== '' ? Number(columnTag) : 0,
      buildingTag: buildingTag !== '' ? Number(buildingTag) : 0,
      questionNumber: questionNumber !== '' ? Number(questionNumber) : 0,
      contentType: contentType.trim(),
      locationType: locationType.map((x) => String(x.id)).join(','),
      status: statusEnabled,
      orgTypes: organizationType.map((x) => String(x.id)),
      threePlStatus,
      additionalModules: ['Observation'],
      ...(roleNames.length > 0 ? { roleNames } : {}),
    };

    return payload;
  };

  // ─── Save handler ─────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setValidationMessage(null);
    const payload = buildPayload();
    console.log('Payload roleNames:', payload.roleNames);
    console.log('[OBS] payload →', JSON.stringify(payload));

    try {
      if (mode === 'edit' && selectedItem?.id) {
        await dispatch(updateObservationQuestion({ id: selectedItem.id, ...payload })).unwrap();
        await dispatch(patchObsStatus({ id: selectedItem.id, status: statusEnabled })).unwrap();
      } else {
        await dispatch(createObservationQuestion(payload)).unwrap();
      }
      onSaveSuccess?.();
      onDismiss();
    } catch (_err) {
      // Error surfaced via Redux saveError
    }
  };

  const handleCancel = () => {
    if (isDirtyRef.current) {
      setShowDiscardConfirm(true);
    } else {
      onDismiss();
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <ConfirmDiscardModal
        visible={showDiscardConfirm}
        onKeepEditing={() => setShowDiscardConfirm(false)}
        onDiscard={() => { setShowDiscardConfirm(false); onDismiss(); }}
      />
      <LHOverlay
        visible={visible}
        onDismiss={handleCancel}
        title={mode === 'edit' ? 'Edit Observation' : 'Add Observation'}
        size="medium"
        position="center"
        close={true}
      >
      <View style={styles.overlayWrapper}>
        {validationMessage ? (
          <View style={[styles.saveErrorBox, isDark && { backgroundColor: colors.statusBackground.error, borderLeftColor: colors.status.error }]}>
            <MaterialIcons
              name="error-outline"
              size={16}
              color={isDark ? colors.status.error : '#C62828'}
              style={{ marginRight: 8 }}
            />
            <Text style={[styles.saveErrorText, isDark && { color: colors.status.error }]}>{validationMessage}</Text>
          </View>
        ) : null}

        <ScrollView style={styles.scrollContent} contentContainerStyle={styles.overlayContent}>

          {/* Status Toggle — edit mode only */}
          {mode === 'edit' && (
            <View style={styles.statusRow}>
              <Text style={[styles.statusLabel, isDark && { color: colors.text.primary }]}>Status:</Text>
              <Switch
                value={statusEnabled}
                onValueChange={(val) => { isDirtyRef.current = true; setStatusEnabled(val); }}
                thumbColor="#FFFFFF"
                trackColor={{ false: '#CCCCCC', true: '#2E7D32' }}
              />
              <Text style={[styles.statusValue, { color: statusEnabled ? '#2E7D32' : '#757575' }]}>
                {statusEnabled ? 'Enabled' : 'Disabled'}
              </Text>
            </View>
          )}

          {/* ── Assessment Text ──────────────────────────────────────────────── */}
          <View style={styles.assessmentSection}>
            <View style={[styles.sectionTitleContainer, isDark && { borderBottomColor: colors.border.divider }]}>
              <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary }]}>Assessment Text</Text>
            </View>

            {/* Observation Description → `observation` */}
            <View style={styles.inputGroup}>
              <View style={styles.labelRow}>
                <Text style={[styles.inputLabel, isDark && { color: colors.text.primary }]}>
                  Observation Description{' '}
                  <Text style={[styles.greyText, isDark && { color: colors.text.secondary }]}>(negative observation statement)</Text>
                </Text>
                <Text style={styles.requiredLabel}>*Required</Text>
              </View>
              <LhTextInput
                value={observation}
                onChangeText={(val) => {
                  setObservation(val);
                  clearFieldError('observation');
                }}
                placeholder="e.g., Employee did not honk at the intersection"
                multiline
                numberOfLines={3}
                editable={!isTextLocked}
                style={styles.textInput}
                wrapperStyle={[styles.inputWrapper, fieldErrors.observation && styles.inputError, isTextLocked && styles.inputLocked]}
              />
              <View style={styles.charCountRow}>
                {isTextLocked && <Text style={[styles.lockedHelperText, isDark && { color: colors.text.secondary }]}>Locked 24 hours after creation.</Text>}
                <Text style={[styles.charCount, isDark && { color: colors.text.secondary }]}>{observation.length}/500</Text>
              </View>
            </View>

            {/* Assessment Question → `question` */}
            <View style={styles.inputGroup}>
              <View style={styles.labelRow}>
                <Text style={[styles.inputLabel, isDark && { color: colors.text.primary }]}>Assessment Question</Text>
                <Text style={styles.requiredLabel}>*Required</Text>
              </View>
              <LhTextInput
                value={question}
                onChangeText={(val) => {
                  setQuestion(val);
                  clearFieldError('question');
                }}
                placeholder="e.g., Did the employee honk at the intersection?"
                multiline
                numberOfLines={3}
                editable={!isTextLocked}
                style={styles.textInput}
                wrapperStyle={[styles.inputWrapper, fieldErrors.question && styles.inputError, isTextLocked && styles.inputLocked]}
              />
              <View style={styles.charCountRow}>
                {isTextLocked && <Text style={[styles.lockedHelperText, isDark && { color: colors.text.secondary }]}>Locked 24 hours after creation.</Text>}
                <Text style={[styles.charCount, isDark && { color: colors.text.secondary }]}>{question.length}/500</Text>
              </View>
            </View>

            <View style={[styles.infoBox, isDark && { backgroundColor: colors.statusBackground.info, borderColor: colors.brand.primary }]}>
              <MaterialIcons name="info" size={20} color={isDark ? colors.brand.primary : '#034895'} style={styles.infoIcon} />
              <Text style={[styles.infoText, isDark && { color: colors.brand.primary }]}>
                The Observation Description is the default text. If an Assessment Question is
                provided, question-based views will display it instead.
              </Text>
            </View>
          </View>

          {/* ── Shared Attributes ─────────────────────────────────────────────── */}
          <View style={styles.sharedAttributesSection}>
            <View style={[styles.sectionTitleContainer, isDark && { borderBottomColor: colors.border.divider }]}>
              <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary }]}>Shared Attributes</Text>
            </View>

            {/* Row 1: Location Type & Organization Type */}
            <View style={[styles.dropdownRow, { zIndex: 30 }]}>
              <View style={[styles.dropdownColumn, { zIndex: 30 }, fieldErrors.locationType && styles.dropdownErrorWrapper]}>
                <View style={styles.labelRow}>
                  <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Location Type</Text>
                  <Text style={styles.requiredLabel}>*Required</Text>
                </View>
                <MultiSelectDropdown
                  label=""
                  options={LOCATION_TYPE_OPTIONS}
                  selected={locationType}
                  onChange={(val) => { setLocationType(val); clearFieldError('locationType'); }}
                  placeholder="Select Location Type"
                />
              </View>
              <View style={[styles.dropdownColumn, { zIndex: 30 }, fieldErrors.organizationType && styles.dropdownErrorWrapper]}>
                <View style={styles.labelRow}>
                  <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Organization Type</Text>
                  <Text style={styles.requiredLabel}>*Required</Text>
                </View>
                <MultiSelectDropdown
                  label=""
                  options={ORGANIZATION_TYPE_OPTIONS}
                  selected={organizationType}
                  onChange={(val) => { setOrganizationType(val); clearFieldError('organizationType'); }}
                  placeholder="Select Organization Type"
                />
              </View>
            </View>

            {/* Row 2: 3PL Status & Current Category */}
            <View style={[styles.dropdownRow, { zIndex: 20 }]}>
              <View style={[styles.dropdownColumn, { zIndex: 20 }, fieldErrors.threeplStatus && styles.dropdownErrorWrapper]}>
                <View style={styles.labelRow}>
                  <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>3PL Status</Text>
                  <Text style={styles.requiredLabel}>*Required</Text>
                </View>
                <LHDropDown
                  label=""
                  childList={THREE_PL_OPTIONS}
                  selectedValue={threeplStatus}
                  setSelectedValue={(val: any) => {
                    setThreeplStatus(val);
                    clearFieldError('threeplStatus');
                  }}
                  placeholder="Select 3PL Status"
                />
              </View>
              <View style={[styles.dropdownColumn, { zIndex: 20 }, fieldErrors.sparcCategory && styles.dropdownErrorWrapper]}>
                <View style={styles.labelRow}>
                  <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Category</Text>
                  <Text style={styles.requiredLabel}>*Required</Text>
                </View>
                <MultiSelectDropdown
                  label=""
                  options={SPARC_CATEGORY_OPTIONS}
                  selected={sparcCategory}
                  onChange={(val) => { setSparcCategory(val); clearFieldError('sparcCategory'); }}
                  placeholder="Select Category"
                  singleSelect
                />
              </View>
            </View>

            {/* Row 3: SPARC Category
            <View style={[styles.dropdownRow, { zIndex: 10 }]}>
              <View style={[styles.dropdownColumn, { zIndex: 10 }]}>
                <LHDropDown
                  label="SPARC Category"
                  childList={SPARC_CATEGORY_OPTIONS}
                  selectedValue={sparcCategory}
                  setSelectedValue={setSparcCategory}
                  placeholder="Select SPARC Category"
                />
              </View>
              <View style={styles.dropdownColumn} />
            </View>*/}
          </View> 

          {/* ── Observation Attributes ────────────────────────────────────────── */}
          <View style={styles.observationAttributesSection}>
            <Text style={[styles.attributesSectionTitle, isDark && { color: colors.text.primary }]}>Observation Attributes</Text>

            {/* Row 1: Observation Type & Section */}
            <View style={[styles.attributeRow, { zIndex: 30 }]}>
              <View style={[styles.attributeColumn, { zIndex: 30 }, fieldErrors.observationTypes && styles.dropdownErrorWrapper]}>
                <View style={styles.labelRow}>
                  <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Observation Type</Text>
                  <Text style={styles.requiredLabel}>*Required</Text>
                </View>
                <MultiSelectDropdown
                  label=""
                  options={OBSERVATION_TYPE_OPTIONS}
                  selected={observationTypes}
                  onChange={(val) => {
                    setObservationTypes(val);
                    clearFieldError('observationTypes');
                  }}
                  placeholder="Select Observation Type"
                />
              </View>
              <View style={[styles.attributeColumn, { zIndex: 30 }, fieldErrors.section && styles.dropdownErrorWrapper]}>
                <View style={styles.labelRow}>
                  <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Section</Text>
                  <Text style={styles.requiredLabel}>*Required</Text>
                </View>
                <MultiSelectDropdown
                  label=""
                  options={OBSERVATION_SECTION_OPTIONS}
                  selected={section}
                  onChange={(val) => {
                    setSection(val);
                    clearFieldError('section');
                  }}
                  placeholder="Select Section"
                />
              </View>
            </View>

            {/* Row 2: Role — only visible once all 3 Shared Attributes are selected */}
            {sharedAttributesComplete && (
              <View style={[styles.attributeRow, { zIndex: 20 }]}>
                <View style={[styles.attributeColumn, { zIndex: 20 }, fieldErrors.roles && styles.dropdownErrorWrapper]}>
                  {rolesLoading ? (
                    <Text style={styles.rolesLoadingText}>Loading roles…</Text>
                  ) : (
                    <>
                      <View style={styles.labelRow}>
                        <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Roles</Text>
                        <Text style={styles.requiredLabel}>*Required</Text>
                      </View>
                      <MultiSelectDropdown
                        label=""
                        options={filteredRoles}
                        selected={roles}
                        onChange={(val) => {
                          setRoles(val);
                          clearFieldError('roles');
                        }}
                        placeholder={filteredRoles.length === 0 ? 'No roles available' : 'Select Roles'}
                      />
                    </>
                  )}
                </View>
                <View style={styles.attributeColumn} />
              </View>
            )}
          </View>

        </ScrollView>

        {/* ── Footer ────────────────────────────────────────────────────────────── */}
        <View style={styles.overlayFooter}>
          {saveError && visible ? (
            <View style={[styles.saveErrorBox, isDark && { backgroundColor: colors.statusBackground.error, borderLeftColor: colors.status.error }]}>
              <MaterialIcons
                name="error-outline"
                size={16}
                color={isDark ? colors.status.error : '#C62828'}
                style={{ marginRight: 8 }}
              />
              <Text style={[styles.saveErrorText, isDark && { color: colors.status.error }]}>{saveError}</Text>
            </View>
          ) : null}
          <View style={styles.footerButtons}>
            <LhButton
              text="Cancel"
              onPress={handleCancel}
              variant="secondary"
              disabled={isSaving}
            />
            <LhButton
              text="Save"
              onPress={handleSave}
              variant="primary"
              isLoading={isSaving}
              disabled={isSaving}
            />
          </View>
        </View>
      </View>
    </LHOverlay>
    </>
  );
};

export default ObservationAddOverlay;

// ─── Styles ───────────────────────────────────────────────────────────────────

